import React, { useEffect, useRef, useState } from 'react'
import { ReactSVG } from 'react-svg'
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js'
import Pack from '../../assets/Icons/Pack.svg'
import Tick from '../../assets/Icons/Tick.svg'
import Spin from '../../assets/Icons/Spin.svg'
import { BnSubpage } from '../Page'
import { HomeButton, Account } from '../Home'
import { KeyboardButton } from '../Keyboard'
import { stripePromise } from '../../classes/Stripe'
import './index.css'

export function StripePaymentForm ({ onConfirmed, isCurrent }) {
  const stripe = useStripe()
  const elements = useElements()
  const submitting = useRef(false)
  const mounted = useRef(true)
  useEffect(() => () => { mounted.current = false }, [])
  const [busy, setBusy] = useState(false)
  const [complete, setComplete] = useState(false)
  const [unavailable, setUnavailable] = useState(false)
  const buy = async () => {
    if (!stripe || !elements || !complete || submitting.current || !isCurrent()) return
    submitting.current = true
    setBusy(true)
    try {
      const result = await stripe.confirmPayment({
        elements, redirect: 'if_required',
        confirmParams: { return_url: window.location.origin + '/' }
      })
      if (!mounted.current || !isCurrent()) return
      if (result.error) return
      if (['succeeded', 'processing'].includes(result.paymentIntent?.status)) {
        await onConfirmed(result.paymentIntent)
        return
      } else {
        setUnavailable(true)
      }
    } catch {
      // An ambiguous confirmation must not offer another payment from this form.
      if (mounted.current && isCurrent()) setUnavailable(true)
    } finally {
      if (mounted.current && isCurrent()) setBusy(false)
      submitting.current = false
    }
  }
  if (unavailable) return null
  return <div className='bnPaymentMethodInput'>
    <PaymentElement onChange={event => setComplete(event.complete)}
      onLoadError={() => { setComplete(false); setUnavailable(true) }}/>
    <KeyboardButton label={busy ? 'Processing' : 'Buy'} icon={Tick}
      action={stripe && elements && complete && !busy ? buy : undefined}/>
  </div>
}

export const WordPackButton = ({ wordPack, isPayment, onSelect, children }) => (
  <div className={'keyboardWordPack' + (isPayment ? ' keyboardWordPackPayment' : '')}>
    <div className='keyboardWordPackRow1' role={isPayment ? undefined : 'button'}
      tabIndex={isPayment ? undefined : 0} onClick={isPayment ? undefined : onSelect}
      onKeyDown={isPayment ? undefined : event => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onSelect() }
      }}>
      <div className='keyboardWordPackLeft'>
        <div className='keyboardWordPackBuyButtonIcon'><ReactSVG src={Pack}/></div>
        <div className='keyboardWordPackWordCount'>{wordPack.name}</div>
      </div>
      <div className='keyboardWordPackRight'><div className='keyboardWordPackWordPrice'>{wordPack.displayPrice}</div></div>
    </div>
    {children}
  </div>
)

export class WordPackPurchase extends BnSubpage {
  constructor (props) {
    super(props)
    this.state = { ...this.state, wordPacks: [], loading: true, paymentStatus: '' }
    this.uid = props.me.self?.uid
    this.authSession = props.me.nativeAuthGeneration?.snapshot()
    this.unmounted = false
    this.buying = false
    this.checkoutSequence = 0
    this.abort = new AbortController()
  }

  isCurrent = () => !this.unmounted && this.uid === this.props.me.self?.uid &&
    (!this.authSession || this.props.me.nativeAuthGeneration.isCurrent(this.authSession, this.props.me.self?.uid))

  componentDidMount () {
    this.loadCatalog()
    this.authSub = this.props.me.observeSelf().subscribe(() => {
      if (!this.isCurrent()) {
        this.abort.abort()
        this.clearCatalog()
      }
    })
    document.addEventListener('visibilitychange', this.onVisible)
  }

  componentWillUnmount () {
    this.unmounted = true
    this.abort.abort()
    this.authSub?.unsubscribe()
    document.removeEventListener('visibilitychange', this.onVisible)
  }

  onVisible = () => {
    if (document.visibilityState === 'visible' && this.isCurrent()) {
      this.props.me.tokenPurchases.resume().catch(() => {})
    }
  }

  clearCatalog = () => this.setState({ catalog: null, wordPacks: [], paymentIntent: null, stripeClient: null, loading: false })

  loadCatalog = async () => {
    if (!this.isCurrent() || this.loadingCatalog) return
    this.loadingCatalog = true
    this.setState({ loading: true })
    try {
      const catalog = await this.props.me.getAvailableTokenPacks()
      if (!this.isCurrent()) return
      const products = catalog.products.filter(pack => !this.props.wordPack || pack.productId === this.props.wordPack.productId)
        .sort((a, b) => a.tokenCount - b.tokenCount)
      this.setState({ catalog, wordPacks: products, loading: false })
      if (catalog.provider === 'apple') this.props.me.tokenPurchases.resume().catch(() => {})
      // Apple screens never create a Stripe customer or payment intent.
      if (this.props.wordPack && products.length && catalog.provider === 'stripe') {
        this.setState({ loading: true })
        const client = stripePromise(catalog.publishableKey)
        if (!(await client)) throw new Error('Website payments could not be loaded.')
        if (!this.isCurrent()) return
        const paymentIntent = await this.props.me.createCustomerPaymentIntent(products[0].productId)
        if (!this.isCurrent()) return
        if (paymentIntent.error || !paymentIntent.id || !paymentIntent.clientSecret) throw new Error('Website payments are unavailable. Please try again later.')
        this.setState({ paymentIntent, stripeClient: client, loading: false })
      }
    } catch {
      if (this.isCurrent()) this.clearCatalog()
    } finally { this.loadingCatalog = false }
  }

  purchase = wordPack => {
    if (!this.isCurrent() || this.buying) return
    // BnPage retains the outgoing subpage during its slide-out animation.
    // Every selection needs a fresh checkout, including reopening the same pack.
    const checkoutKey = ++this.checkoutSequence
    this.setState({ subpage: () => <WordPackPurchase key={checkoutKey} title='Token Packs' wordPack={wordPack}
      me={this.props.me} back={this.back} available={this.props.available}/> })
  }

  buy = async wordPack => {
    if (!this.isCurrent() || this.buying || this.state.catalog?.provider !== 'apple') return false
    this.buying = true
    this.setState({ paymentStatus: 'purchase' })
    try {
      return await this.props.me.purchaseWordPack(wordPack, (paymentStatus, message) => {
        if (this.isCurrent()) this.setState({ paymentStatus, paymentStatusMsg: message })
      })
    } catch {
      if (this.isCurrent()) this.setState({ paymentStatus: 'failed', paymentStatusMsg: 'Purchase unavailable. Please try again.' })
      return false
    } finally { this.buying = false }
  }

  buyWithStripe = async payment => {
    if (!this.isCurrent() || this.buying || this.state.catalog?.provider !== 'stripe' || payment.id !== this.state.paymentIntent?.id) return
    this.buying = true
    this.setState({ paymentStatus: 'verify', paymentStatusMsg: 'Waiting for confirmed payment' })
    try {
      const result = await this.props.me.waitForStripePurchase(payment, this.abort.signal)
      if (this.isCurrent()) this.setState(result.refunded
        ? { paymentStatus: 'refunded', paymentStatusMsg: result.remainingTokenCount > 0 ? 'Payment partially refunded' : 'Payment refunded' }
        : { paymentStatus: result.sandbox ? 'sandbox' : 'complete',
          paymentStatusMsg: result.sandbox ? 'Test payment verified. No live tokens were added.' : 'Tokens added' })
    } catch {
      if (this.isCurrent()) this.setState({ paymentStatus: 'verification-pending',
        paymentStatusMsg: 'Payment confirmation is pending. Tokens will appear after confirmation; do not pay again.' })
    } finally { this.buying = false }
  }

  activateReferralCode = () => {
    this.setState({ subpage: () => <Account title='Account' me={this.props.me}
      getSetting={this.props.getSetting} toggleSetting={this.props.toggleSetting} back={this.back}/> })
  }

  renderContent () {
    const { loading, wordPacks, paymentStatus, paymentStatusMsg, catalog } = this.state
    const summary = Boolean(this.props.wordPack)
    const busy = ['purchase', 'verify'].includes(paymentStatus)
    const finished = ['complete', 'sandbox', 'pending', 'verification-pending', 'refunded'].includes(paymentStatus)
    return <div className='bnSubpageContent'>
      <div className='keyboardWordPackContent'>
        <div className='keyboardWordPackHeader'>
          <div className='keyboardWordPackHeaderLabel'>{summary ? 'Payment summary' : 'Add Tokens to your account'}</div>
          <div className='keyboardWordPackHeaderAvailable'>
            <div className='keyboardWordPackHeaderAvailableAmount'>{this.props.available}</div>
            <div className='keyboardWordPackHeaderAvailableLabel'>available</div>
          </div>
        </div>
        <div className='keyboardWordPurchase'>
          {wordPacks.map(pack => <WordPackButton key={pack.productId} wordPack={pack} isPayment={summary}
            onSelect={() => this.purchase(pack)}>
            {summary && <div className='keyboardWordPackRow2'>
              <div className='keyboardWordPackOrderSummary'>
                <div className='keyboardWordPackSummaryLine'><span>Price</span><span>{pack.displayPrice}</span></div>
                <div className='keyboardWordPackSummaryLine keyboardWordPackTotalLine'><span>Total</span><span>{pack.displayPrice}</span></div>
              </div>
              {paymentStatusMsg && paymentStatus !== 'failed' && <p role='status'>{paymentStatusMsg}</p>}
              {!busy && !finished && catalog.provider === 'apple' &&
                <KeyboardButton label='Buy' icon={Tick} action={() => this.buy(pack)}/>}
              {!busy && !finished && catalog.provider === 'stripe' && this.state.paymentIntent &&
                <Elements stripe={this.state.stripeClient} options={{ clientSecret: this.state.paymentIntent.clientSecret }}>
                  <StripePaymentForm onConfirmed={this.buyWithStripe} isCurrent={this.isCurrent}/>
                </Elements>}
              {finished && <KeyboardButton label='Done' icon={Tick} action={this.props.back}/>}
            </div>}
          </WordPackButton>)}
        </div>
        {(loading || busy) && <div className='wordPackSpinContainer'><ReactSVG src={Spin}/></div>}
      </div>
      {!summary && <div className='keyboardWordPackRefer'>
        <HomeButton icon={Pack} label='Refer your friends'
          buttonLabel={<span>Get <span className='FREE'>FREE</span> Token Packs</span>} action={this.activateReferralCode}/>
      </div>}
    </div>
  }
}
